/** Lower bound of the "All" range; the backend treats a lone `from` as open-ended on the other side. */
const ALL_TIME_FROM = '1900-01-01';

/**
 * Drives the platform costs section of the billing overview: loads its rows, and saves and deletes
 * costs. Every successful mutation dispatches `billing-changed`, so the platform result, the income
 * counter and the nav badge read again.
 */
export class PlatformCostsEventAdapter {
  #apiAdapter;
  #component;
  #notificationComponent;
  #reloadPage;
  /** Counts loads, so the answer to an earlier one never replaces a later one. */
  #loads = 0;

  /**
   * @param {() => void} [reloadPage] reloads the app so index.js shows the login screen
   */
  constructor(apiAdapter, component, notificationComponent, reloadPage = location.reload.bind(location)) {
    this.#apiAdapter = apiAdapter;
    this.#component = component;
    this.#notificationComponent = notificationComponent;
    this.#reloadPage = reloadPage;
  }

  connect() {
    document.addEventListener('platform-costs-range', this.#onRange);
    document.addEventListener('platform-cost-save', this.#onSave);
    document.addEventListener('platform-cost-delete', this.#onDelete);
  }

  disconnect() {
    document.removeEventListener('platform-costs-range', this.#onRange);
    document.removeEventListener('platform-cost-save', this.#onSave);
    document.removeEventListener('platform-cost-delete', this.#onDelete);
  }

  /** Shows the costs of the last 12 months, freshly loaded. */
  async showCosts() {
    this.#component.showAll = false;
    await this.#load();
  }

  /** Loads the rows for the range the component shows; a failure is reported and the rows stay as they were. */
  async #load() {
    const load = ++this.#loads;
    try {
      const costs = await this.#apiAdapter.getPlatformCosts(this.#component.showAll ? { from: ALL_TIME_FROM } : {});
      if (load === this.#loads) {
        this.#component.costs = costs;
      }
    } catch (error) {
      if (load === this.#loads) {
        this.#handleError(error, 'Could not load the platform costs');
      }
    }
  }

  #onRange = async () => {
    await this.#load();
  };

  /** Creates the cost when `id` is null, else updates it. A rejected save keeps the form open with the reason shown in it. */
  #onSave = async (event) => {
    const { id, request } = event.detail;
    if (this.#component.saving) return;
    this.#component.saving = true;
    try {
      if (id === null) {
        await this.#apiAdapter.createPlatformCost(request);
      } else {
        await this.#apiAdapter.updatePlatformCost(id, request);
      }
    } catch (error) {
      this.#handleError(error, 'Could not save platform cost', (message) => this.#component.showFormError(message));
      return;
    } finally {
      this.#component.saving = false;
    }
    this.#component.closeForm();
    this.#notificationComponent.show(id === null ? 'Platform cost added' : 'Platform cost updated', 'success');
    await this.#load();
    document.dispatchEvent(new CustomEvent('billing-changed'));
  };

  #onDelete = async (event) => {
    const { id, label } = event.detail;
    try {
      await this.#apiAdapter.deletePlatformCost(id);
    } catch (error) {
      this.#handleError(error, 'Could not delete platform cost');
      return;
    }
    this.#notificationComponent.show(`Platform cost "${label}" deleted`, 'success');
    await this.#load();
    document.dispatchEvent(new CustomEvent('billing-changed'));
  };

  /**
   * Shows why a request failed, once: in the form when `showInForm` can show it there, else in a
   * toast. An expired session always goes to the toast, since the app reloads right after.
   *
   * @param {{status?: number, message: string}} error
   * @param {string} action what failed, shown before the server's message
   * @param {(message: string) => boolean} [showInForm] returns whether it could show the message
   */
  #handleError(error, action, showInForm) {
    if (error.status === 401) {
      this.#component.closeForm();
      this.#notificationComponent.show('Session expired. Please login again.', 'error');
      setTimeout(() => this.#reloadPage(), 2000);
    } else if (!showInForm?.(`${action}: ${error.message}`)) {
      this.#notificationComponent.show(`${action}: ${error.message}`, 'error');
    }
  }
}
